import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["form", "submit"]
  static values = {
    siteKey: String,
    action: String,
    enabled: Boolean
  }

  connect() {
    if (!this.enabledValue) {
      return
    }

    this.waitForRecaptcha()

    // Back from the thank-you page can restore this page from the browser cache
    // with the button still disabled; make the form usable again.
    this.resetOnRestore = (event) => { if (event.persisted) this.resetSubmitting() }
    window.addEventListener("pageshow", this.resetOnRestore)
  }

  disconnect() {
    if (this.resetOnRestore) window.removeEventListener("pageshow", this.resetOnRestore)
  }

  // The button is disabled from the first tap until the page moves on, so the
  // customer can see the form is sending and can't send it twice.
  showSubmitting(submitter) {
    this.submitButton = submitter || this.formTarget.querySelector('[type="submit"]')
    if (!this.submitButton) return

    this.submitButtonLabel = this.submitButtonText
    this.submitButton.disabled = true
    this.submitButtonText = "Submitting…"
  }

  resetSubmitting() {
    this.submitting = false
    if (!this.submitButton) return

    this.submitButton.disabled = false
    this.submitButtonText = this.submitButtonLabel
  }

  get submitButtonText() {
    return this.submitButton.tagName === "INPUT" ? this.submitButton.value : this.submitButton.textContent
  }

  set submitButtonText(text) {
    if (this.submitButton.tagName === "INPUT") {
      this.submitButton.value = text
    } else {
      this.submitButton.textContent = text
    }
  }

  waitForRecaptcha() {
    this.recaptchaReady = false

    if (this.checkRecaptchaReady()) {
      this.recaptchaReady = true
      return
    }

    let attempts = 0
    const maxAttempts = 20
    const pollInterval = 250

    const poll = () => {
      attempts++

      if (this.checkRecaptchaReady()) {
        this.recaptchaReady = true
        return
      }

      if (attempts < maxAttempts) {
        setTimeout(poll, pollInterval)
      } else {
        this.recaptchaReady = false
      }
    }

    setTimeout(poll, pollInterval)
  }

  checkRecaptchaReady() {
    return typeof grecaptcha !== 'undefined' &&
           grecaptcha.ready &&
           typeof grecaptcha.execute === 'function'
  }

  async handleSubmit(event) {
    if (!this.enabledValue) {
      return
    }

    // A second tap while the token is fetched or the page is posting would send
    // the form twice, and the resort gets the same enquiry twice (B72).
    if (this.submitting) {
      event.preventDefault()
      return
    }

    // Check if we already have a token (avoid duplicate submissions)
    const existingToken = this.formTarget.querySelector('input[name="g-recaptcha-response"]')
    if (existingToken && existingToken.value) {
      return
    }

    event.preventDefault()
    this.submitting = true
    this.showSubmitting(event.submitter)

    // If reCAPTCHA isn't ready, try one more check
    if (!this.recaptchaReady && this.checkRecaptchaReady()) {
      this.recaptchaReady = true
    }

    if (!this.recaptchaReady) {
      this.formTarget.submit()
      return
    }

    try {
      const token = await this.getRecaptchaToken()
      this.addTokenToForm(token)
      this.formTarget.submit()
    } catch (error) {
      // Submit without token if reCAPTCHA fails
      this.formTarget.submit()
    }
  }

  async getRecaptchaToken() {
    return new Promise((resolve, reject) => {
      if (typeof grecaptcha === 'undefined') {
        reject(new Error('reCAPTCHA not loaded'))
        return
      }

      grecaptcha.ready(() => {
        grecaptcha.execute(this.siteKeyValue, { action: this.actionValue })
          .then(resolve)
          .catch(reject)
      })
    })
  }

  addTokenToForm(token) {
    // Remove existing token field if any
    const existingField = this.formTarget.querySelector('input[name="g-recaptcha-response"]')
    if (existingField) {
      existingField.remove()
    }

    // Add new token field
    const tokenField = document.createElement('input')
    tokenField.type = 'hidden'
    tokenField.name = 'g-recaptcha-response'
    tokenField.value = token
    this.formTarget.appendChild(tokenField)
  }
}